import type { Skill } from 'types/api/appdata';
import type { APIInterventionPATCHRequest, APIInterventionPOSTRequest, Intervention, InterventionForm } from 'types/api/intervention'
import { getDateFromTimeModel, getDateYMD } from './service_date';
import { NullIfEmpty, ValueOrUndefined } from './service_api';
import { useInterventionStore } from '@/stores/gae/store_intervention';

type Status = { label: string; color: ApplicationColors, readonly?: boolean }
const mission_statuses: {
  [K in Intervention['missionStatus']]: Status
} = {
  incomplete: { label: 'Incomplète', color: 'danger' },
  in_progress: { label: "Demande d'info", color: 'warning' },
  complete: { label: 'Complète', color: 'success', readonly: true },
  documents_ok: { label: 'Terminée', color: 'success', readonly: true },
  pending_documents: { label: 'Documents en attente', color: 'warning', readonly: true },
  documents_uploaded: { label: 'Documents envoyés', color: 'warning', readonly: true },
}

const client_statuses: {
  [K in Intervention['customerStatus']]: Status
} = {
  option: { label: 'Option', color: 'warning' },
  confirmed: { label: 'Validé', color: 'success' },
  canceled: { label: 'Annulé', color: 'danger' },
}

const consultant_statuses: {
  [K in Intervention['consultantStatus']]: Status
} = {
  none: { label: 'Aucun intervenant', color: 'danger' },
  pending: { label: 'En attente', color: 'warning' },
  confirmed: { label: 'Validé', color: 'success' },
}

export function getMissionStatus(intervention: Intervention) {
  return mission_statuses[intervention.missionStatus]
}

export function getClientStatus(intervention: Intervention) {
  return client_statuses[intervention.customerStatus]
}

export function getConsultantStatus(intervention: Intervention): Status {
  return consultant_statuses[intervention.consultantStatus]
}


export function getMissionStatusArray() {
  const output: SelectOption[] = []
  Object.entries(mission_statuses).forEach(([key, value]) => {
    output.push({ label: value.label, value: key, readonly: value.readonly })
  })
  return output
}

export function getClientStatusArray() {
  const output: SelectOption[] = []
  Object.entries(client_statuses).forEach(([key, value]) => {
    output.push({ label: value.label, value: key, readonly: value.readonly })
  })
  return output
}

export function getConsultantStatusArray() {
  const output: SelectOption[] = []
  Object.entries(consultant_statuses).forEach(([key, value]) => {
    output.push({ label: value.label, value: key, readonly: value.readonly })
  })
  return output
}

export function getSkillCategories(skills?: Skill[]) {
  if (skills == undefined)
    return []
  return [...new Set(skills.map((item) => item.category))]
}

export function getInterventionDateTime(date_str?: string, time?: string) {
  if (date_str && time) {
    const date = new Date(date_str);
    const [hour, minute] = time.split(':')
    date.setHours(parseInt(hour));
    date.setMinutes(parseInt(minute));
    return date;
  }
}

export function getDayDate(string_date: string) {
  const date = new Date(string_date)
  const options = { month: 'long', day: 'numeric' }
  // Missing option type for typescript.
  // @ts-ignore
  return date.toLocaleDateString('fr-FR', options)
}

export function getContract(form: InterventionForm): APIInterventionPOSTRequest {
  return {
    complement: NullIfEmpty(form.complement),
    date: getDateYMD(form.date),
    dayPart: NullIfEmpty(form.day_part),
    address: NullIfEmpty(form.address),
    city: NullIfEmpty(form.city),
    postcode: NullIfEmpty(form.postcode),
    consultant: form.consultant?.id ?? null,
    startTime: getDateFromTimeModel(form.start) ?? null,
    endTime: getDateFromTimeModel(form.end) ?? null,
    isDocumentFolderSent: form.sent,
    isRemote: form.remote,
    backer: NullIfEmpty(form.sponsor),
    missionStatus: form.mission_status ?? 'incomplete',
    customer: NullIfEmpty(form.client),
    commercialCustomerContact: NullIfEmpty(form.contact_commercial),
    onSiteContact: NullIfEmpty(form.contact),
    customerStatus: ValueOrUndefined(form.client_status) ?? 'option',
    skills: form.skills.map((s) => s.id),
    files: form.files?.map((f) => f.id) ?? undefined,
    referent: form.gae_refered ? parseInt(form.gae_refered) : null,
    salesperson: form.gae_commercial ? parseInt(form.gae_commercial) : null,
    comment: NullIfEmpty(form.comment),
    additionalFees: parseFloat(form.additional_fees),
    zone: form.zone ? parseInt(form.zone) : null,
    ignoredSteps: form.ignored_steps,
    clientEmail: NullIfEmpty(form.client_email),
    npsEmail: NullIfEmpty(form.nps_email),
    childs: form.childs.map(e => e.id)
  }
}

export function interventionToPatchRequest(intervention: Intervention): APIInterventionPATCHRequest {
  return {
    complement: intervention.complement,
    date: intervention.date,
    dayPart: intervention.dayPart,
    address: intervention.address,
    city: intervention.city,
    postcode: intervention.postcode,
    consultant: intervention.consultant?.id ?? null,
    startTime: intervention.startTime ?? null,
    endTime: intervention.endTime ?? null,
    isDocumentFolderSent: intervention.isDocumentFolderSent,
    isRemote: intervention.isRemote,
    backer: intervention.backer,
    missionStatus: intervention.missionStatus,
    customer: intervention.customer,
    commercialCustomerContact: intervention.commercialCustomerContact,
    onSiteContact: intervention.onSiteContact,
    customerStatus: intervention.customerStatus,
    skills: intervention.skills.map((s) => s.id),
    files: intervention.files?.map((f) => f.id) ?? undefined,
    referent: intervention.referent?.id ?? null,
    salesperson: intervention.salesperson?.id ?? null,
    comment: intervention.comment,
    additionalFees: intervention.additionalFees,
    zone: intervention.zone?.level ?? null,
    ignoredSteps: intervention.ignoredSteps,
    clientEmail: intervention.clientEmail,
    npsEmail: intervention.npsEmail,
    childs: intervention.childs?.map(id => id) ?? [],
    documentReferrer: intervention.documentReferrer
  }
}

export function resyncRelationships(intervention: Intervention) {
  const store = useInterventionStore()
  store.getInterventions.forEach(month => {
    for (const month_intervention of month.interventions) {
      if (month_intervention.parentIntervention === intervention.id) {
        month_intervention.parentIntervention = undefined;
      }

      if (intervention.childs?.includes(month_intervention.id)) {
        month_intervention.parentIntervention = intervention.id
        // remove all steps except attendance for child intervention
        month_intervention.ignoredSteps = [2, 3, 4, 5, 6, 7]
      }
    }
  })
}
// Categories are detected by NAME, never by id: ids differ between environments
// (GESTION DE CRISE is 7 in preprod but 8 in prod, where 7 is COMMERCIAL).
export const CATEGORY_CRISE = 'GESTION DE CRISE'
export const CATEGORY_CONSULTATION = 'CONSULTATION'

// Categories whose only deliverable is the document filled in by the consultant:
// no trainees, no groups, no other step. Each document has its own step in
// ignored_steps: 8 = rapport de fin de mission, 9 = évaluation post-consultation.
const CONSULTANT_REPORT_STEPS: Record<string, number> = {
  [CATEGORY_CRISE]: 8,
  [CATEGORY_CONSULTATION]: 9
}

export function consultantReportStep(name?: string | null): number | null {
  return CONSULTANT_REPORT_STEPS[(name ?? '').toUpperCase()] ?? null
}

function isSingleReportCategory(name?: string | null): boolean {
  return consultantReportStep(name) !== null
}

function hasCategory(intervention: Intervention, name: string): boolean {
  return intervention.serviceCategories.some((c) => c.name === name)
}

export function isCrise(intervention: Intervention): boolean {
  return hasCategory(intervention, CATEGORY_CRISE)
}

export function isConsultation(intervention: Intervention): boolean {
  return hasCategory(intervention, CATEGORY_CONSULTATION)
}

export function hasSingleConsultantReport(intervention: Intervention): boolean {
  return intervention.serviceCategories.some((c) => isSingleReportCategory(c.name))
}
